/**
 * SiteAccessGate - site-wide ring-fence + maintenance mode.
 * - Signed-out visitors may only see the home page, policy pages and sign-in flows.
 * - While maintenance is on, only admins can use the site; everyone else sees the maintenance page.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Navigate, useLocation, Link } from 'react-router-dom';
import { Wrench } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE } from '../config/api';

const ADMIN_ROLES = ['super_admin', 'admin', 'org_admin'];
const POLICY_PATHS = ['/responsible-ai', '/privacy-policy', '/terms-of-use', '/hitl-governance'];
const AUTH_PATHS = ['/login', '/enterprise-sso', '/sso/callback', '/auth/callback', '/lti/launch'];

export function MaintenancePage({ message }) {
  return (
    <div className="min-h-screen bg-navigate-dark flex items-center justify-center px-4" data-testid="maintenance-page">
      <div className="text-center max-w-lg">
        <div className="mx-auto mb-6 h-16 w-16 rounded-full bg-navigate-surface border border-zinc-800 flex items-center justify-center">
          <Wrench className="h-7 w-7 text-navigate-gold" />
        </div>
        <h1 className="font-heading text-4xl font-bold text-white mb-3">We&apos;ll be back soon</h1>
        <p className="text-zinc-400 mb-8" data-testid="maintenance-message">{message}</p>
        <Link to="/login" className="text-sm text-zinc-500 hover:text-navigate-gold" data-testid="maintenance-admin-login">Admin sign in</Link>
      </div>
    </div>
  );
}

export function useMaintenance() {
  const { pathname } = useLocation();
  const [state, setState] = useState({ enabled: false, message: '', loaded: false });
  useEffect(() => {
    const load = () => axios.get(`${API_BASE}/api/system/maintenance`)
      .then((r) => setState({ ...r.data, loaded: true }))
      .catch(() => setState((s) => ({ ...s, loaded: true })));
    load();
    window.addEventListener('maintenance-changed', load);
    return () => window.removeEventListener('maintenance-changed', load);
  }, [pathname]);
  return state;
}

export default function SiteAccessGate({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const maintenance = useMaintenance();
  const path = location.pathname.replace(/\/+$/, '') || '/';
  const isAuthPath = AUTH_PATHS.includes(path);
  const isPolicy = POLICY_PATHS.includes(path);
  const isAdmin = ADMIN_ROLES.includes((user?.role || '').toLowerCase());

  if (loading || !maintenance.loaded) return null;

  if (maintenance.enabled && !isAdmin && !isAuthPath && !isPolicy) {
    return <MaintenancePage message={maintenance.message} />;
  }

  if (!user && path !== '/' && !isAuthPath && !isPolicy) {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirect=${redirect}`} replace />;
  }

  return (
    <>
      {maintenance.enabled && isAdmin && (
        <div className="sticky top-0 z-[70] bg-amber-500 text-black text-sm text-center py-1.5 px-4 font-medium" data-testid="maintenance-admin-banner">
          Maintenance mode is ON. Only admins can use the site. Turn it off in Admin → Maintenance Mode.
        </div>
      )}
      {children}
    </>
  );
}
